import type { TableCardsView } from '@gitbook/doc-core';
import { PaletteCommandType, type PaletteDef, type PaletteGroupDef } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';

import type { TableViewCommandsParams, TableViewSpec } from '../types';
import { pickColumnDefinitions } from '../utils';
import { onKeyDown } from './keyboard';
import { TableCardsViewComponent } from './TableCardsView';

export const cardsSpec: TableViewSpec<TableCardsView> = {
    icon: Icon.Cards3Columns,
    title: 'Cards',
    recordName: 'card',
    definitionName: 'field',
    selectableView: false,
    ViewComponent: TableCardsViewComponent,
    commands: getGridViewCommands,
    onKeyDown,
};

/**
 * Get the commands to control the view.
 */
function getGridViewCommands(params: TableViewCommandsParams<TableCardsView>): PaletteGroupDef[] {
    const { view, setView } = params;
    return [
        {
            key: 'cards.definitions',
            commands: [
                {
                    key: 'cards.selectTarget',
                    title: 'Target link',
                    icon: Icon.AddLink,
                    descendant: () => getSelectTargetCommands(params),
                },
            ],
        },
        {
            key: 'cards.size',
            commands: [
                {
                    key: 'cards.size.medium',
                    title: 'Medium',
                    icon: Icon.Cards3Columns,
                    type: PaletteCommandType.CHECKBOX,
                    selected: view.cardSize === 'medium',
                    trigger: () => {
                        const [createPalette] = setView({
                            ...view,
                            cardSize: 'medium',
                        });
                        return createPalette();
                    },
                },
                {
                    key: 'cards.size.large',
                    title: 'Large',
                    icon: Icon.Cards2Columns,
                    type: PaletteCommandType.CHECKBOX,
                    selected: view.cardSize === 'large',
                    trigger: () => {
                        const [createPalette] = setView({
                            ...view,
                            cardSize: 'large',
                        });
                        return createPalette();
                    },
                },
            ],
        },
        {
            key: 'cards.options',
            commands: [
                {
                    key: 'cards.hideColumnTitle',
                    title: 'Show labels',
                    icon: Icon.EyeOff,
                    type: PaletteCommandType.SWITCH,
                    enabled: !view.hideColumnTitle,
                    trigger: () => {
                        const [createPalette] = setView({
                            ...view,
                            hideColumnTitle: !view.hideColumnTitle,
                        });

                        return createPalette();
                    },
                },
                {
                    key: 'cards.definitions',
                    title: 'Fields',
                    icon: Icon.EyeOff,
                    descendant: () => getToggleColumnCommands(params),
                },
            ],
        },
    ];
}

function getSelectTargetCommands(params: TableViewCommandsParams<TableCardsView>): PaletteDef {
    const { view, definition, setView } = params;

    return pickColumnDefinitions(
        definition,
        (selected) => {
            setView({
                ...view,
                targetDefinition: selected[0],
            });
        },
        {
            multiple: false,
            search: 'Select column for target…',
            selected: view.targetDefinition ? [view.targetDefinition] : [],
            filterDefinition: (column) => column.type === 'content-ref',
        }
    );
}

function getToggleColumnCommands(params: TableViewCommandsParams<TableCardsView>): PaletteDef {
    const { view, definition, setView } = params;

    return pickColumnDefinitions(
        definition,
        (selected) => {
            const [, newParams] = setView({
                ...view,
                columns: selected,
            });

            return getToggleColumnCommands(newParams);
        },
        {
            multiple: true,
            search: 'Select visible columns…',
            selected: view.columns,
        }
    );
}
